import "./styles.scss";

import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconSchool } from "@tabler/icons-react";

import { Text } from "@fragments";
import SidebarItem from "../../_components/SidebarItem";

import useCreatorMentorings from "./useCreatorMentorings";

/**
 * Creator shortcut to their mentorings (§6.0). Lives in the sidebar admin
 * section, just below "Banking", and renders for everyone (the admin section
 * always renders). Three states:
 *   0          → nothing
 *   exactly 1  → direct link to that mentoring, with its program banner
 *   2+         → "Mentorias" with the first 3 program banners stacked
 * Renders nothing while loading so the sidebar doesn't flicker an empty slot.
 */
export default function CreatorMentoringItem() {
  const { t } = useTranslation("shell");
  const location = useLocation();
  const { mentorings, loading } = useCreatorMentorings();

  if (loading || mentorings.length === 0) return null;

  const active = location.pathname.includes("/mentoring");

  // Exactly one → behave like a normal SidebarItem pointing straight at it.
  if (mentorings.length === 1) {
    const { id, program } = mentorings[0];
    return (
      <SidebarItem
        text={program?.name ?? t("sidebar.mentoring")}
        left={<Thumb url={program?.banner?.url} />}
        onClick={() => window.navigate(`/mentoring/${id}`)}
        active={active}
      />
    );
  }

  // Two or more → a cluster of overlapping banners + a generic "Mentorias" label.
  const stacked = mentorings.slice(0, 3);
  return (
    <div
      className={`cs-sidebar-item cmi-multi ${active ? "active" : ""}`}
      onClick={() => window.navigate("/mentoring")}
    >
      <div className="cmi-stack">
        {stacked.map((m, i) => (
          <span className="cmi-stack-item" style={{ "--i": i }} key={m.id}>
            <Thumb url={m.program?.banner?.url} bare />
          </span>
        ))}
      </div>
      <div className="sidebar-item-main">
        <Text.h6 weight={active ? 600 : 400}>{t("sidebar.mentorings")}</Text.h6>
      </div>
    </div>
  );
}

// Program banner thumbnail with an icon fallback. `bare` drops the rounded frame
// for the stacked cluster, where the wrapper supplies the frame instead.
function Thumb({ url, bare = false }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={bare ? "cmi-thumb-img cmi-thumb-img--bare" : "cmi-thumb-img"}
        loading="lazy"
      />
    );
  }
  return (
    <span className={`cmi-thumb-fallback${bare ? " cmi-thumb-fallback--bare" : ""}`}>
      <IconSchool size={15} />
    </span>
  );
}
